import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { withStyles } from '../tools/withStyles';
import { Main } from '../components/Main';
import { Secuence } from '../components/Secuence';
import { Text } from '../components/Text';
import { Fader } from '../components/Fader';
import { Link } from '../components/Link';
import { Button } from '../components/Button';

const money = value => `₡${String(value).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;

const images = {
  firma: 'https://www.imperioanimecr.com/wp-content/uploads/2026/10/foto-firma-1024x1024.jpg',
  disco: 'https://www.imperioanimecr.com/wp-content/uploads/2026/10/foto-disco-37000-1024x1024.jpg',
  meet: 'https://www.imperioanimecr.com/wp-content/uploads/2026/10/meet-vip-1024x1024.jpg',
  meetDisco: 'https://www.imperioanimecr.com/wp-content/uploads/2026/10/meet-disco-vinyl-1024x1024.jpg'
};

const links = {
  firma: 'https://www.imperioanimecr.com/p/foto-y-firma-poster-oficial/',
  disco: 'https://www.imperioanimecr.com/p/foto-y-disco-autografiado/',
  meet: 'https://www.imperioanimecr.com/p/meet-greet-foto-firma-vip/',
  meetDisco: 'https://www.imperioanimecr.com/p/meet-greet-foto-firma-y-disco-vip/',
  ticket: 'https://www.omticket.com/'
};

const tiers = [
  {
    name: 'VIP',
    zone: 'Nerv Command',
    price: '₡35.000',
    amount: 35000,
    next: 'Sube a ₡40.000',
    detail: 'Ubicación preferencial frente a tarima.'
  },
  {
    name: 'Preferencial',
    zone: 'Tokyo-3',
    price: '₡20.000',
    amount: 20000,
    next: 'Sube a ₡25.000',
    detail: 'De pie en la gramilla, frente a la tarima principal. La más pedida.'
  },
  {
    name: 'Platea Central',
    zone: 'Dogma',
    price: '₡12.500',
    amount: 12500,
    next: 'Sube a ₡15.500',
    detail: 'Ubicación sentada en la gradería central.'
  },
  {
    name: 'General',
    zone: 'Eva Bay',
    price: '₡7.000',
    amount: 7000,
    next: 'Sube a ₡8.500',
    detail: 'Ubicación sentada en las graderías laterales.'
  }
];

const styles = theme => ({
  root: {},
  intro: {
    maxWidth: 680
  },
  block: {
    display: 'block',
    width: '100%'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    margin: [20, 'auto', 0],
    maxWidth: 760
  },
  card: {
    margin: 0,
    padding: 16,
    border: `1px solid ${theme.color.background.light}`,
    cursor: 'pointer',
    textAlign: 'inherit',
    background: 'transparent',
    color: 'inherit',
    font: 'inherit',
    width: '100%'
  },
  picked: {
    borderColor: theme.color.secondary.main
  },
  plus: {
    margin: [8, 0, 0],
    fontFamily: theme.typography.primary,
    fontSize: 13,
    letterSpacing: '0.03em',
    color: theme.color.secondary.main
  },
  zone: {
    margin: 0,
    fontFamily: theme.typography.secondary,
    fontSize: 12,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: theme.color.secondary.main
  },
  price: {
    margin: [8, 0],
    fontFamily: theme.typography.primary,
    fontSize: 28,
    color: theme.color.heading.main
  },
  next: {
    margin: 0,
    fontSize: 13,
    color: theme.color.text.main
  },
  seller: {
    display: 'block',
    width: 160,
    margin: [32, 'auto', 16]
  },
  vip: {
    position: 'relative',
    borderColor: theme.color.secondary.main,
    boxShadow: `0 0 14px ${theme.color.secondary.main}`
  },
  frame: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    overflow: 'visible',

    '& rect': {
      fill: 'none',
      stroke: theme.color.secondary.main,
      strokeWidth: 2,
      vectorEffect: 'non-scaling-stroke',
      strokeDasharray: '24 76',
      animation: 'arwesTrace 2.6s linear infinite'
    }
  },
  steps: {
    display: 'flex',
    justifyContent: 'center',
    gap: 28,
    margin: [8, 0, 20],
    fontFamily: theme.typography.primary,
    fontSize: 12,
    letterSpacing: '0.12em',
    textTransform: 'uppercase'
  },
  step: {
    color: theme.color.text.main,
    opacity: 0.45
  },
  stepOn: {
    color: theme.color.secondary.main,
    opacity: 1
  },
  summary: {
    margin: [0, 'auto', 16],
    maxWidth: 520
  },
  summaryLabel: {
    margin: 0,
    fontSize: 12,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: theme.color.secondary.main
  },
  summaryPrice: {
    margin: [4, 0],
    fontFamily: theme.typography.primary,
    fontSize: 22,
    color: theme.color.heading.main
  },
  upsell: {
    margin: [12, 'auto', 0],
    maxWidth: 560,
    padding: 20,
    border: `1px solid ${theme.color.secondary.main}`,
    boxShadow: '0 0 18px rgba(198,255,58,0.18)'
  },
  upsellKicker: {
    margin: 0,
    fontSize: 12,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: theme.color.secondary.main
  },
  upsellTitle: {
    margin: [8, 0, 8],
    fontSize: 26
  },
  total: {
    margin: [8, 0],
    fontFamily: theme.typography.primary,
    fontSize: 32,
    color: theme.color.heading.main
  },
  before: {
    marginRight: 8,
    fontSize: 16,
    color: theme.color.text.main,
    textDecoration: 'line-through'
  },
  save: {
    margin: [0, 0, 8],
    color: theme.color.secondary.main
  },
  upsellText: {
    margin: [0, 'auto', 16],
    maxWidth: 480
  },
  alt: {
    margin: [4, 0, 12],
    fontSize: 14
  },
  skip: {
    margin: [8, 0, 0],
    fontSize: 16
  },
  accept: {
    display: 'inline-block',
    margin: [4, 0, 8],
    padding: [14, 18],
    border: 'none',
    cursor: 'pointer',
    background: theme.color.secondary.main,
    color: theme.color.background.dark,
    fontFamily: theme.typography.primary,
    fontSize: 13,
    letterSpacing: '0.04em',
    lineHeight: 1.3,
    textTransform: 'uppercase',
    textDecoration: 'none',

    '&:hover, &:focus': {
      color: theme.color.background.dark
    }
  },
  note: {
    margin: [0, 0, 8],
    fontSize: 13
  },
  order: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
    margin: [0, 0, 16],
    fontSize: 14
  },
  photo: {
    display: 'block',
    width: 220,
    height: 220,
    objectFit: 'cover',
    margin: [12, 'auto', 8]
  },
  rows: {
    margin: [8, 'auto', 16],
    maxWidth: 320,
    textAlign: 'left'
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: [0, 0, 6],
    fontSize: 15
  },
  rowTotal: {
    marginTop: 8,
    paddingTop: 8,
    borderTop: `1px solid ${theme.color.background.light}`,
    fontFamily: theme.typography.primary,
    color: theme.color.heading.main
  },
  back: {
    margin: [16, 0, 0],
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: theme.color.text.main,
    font: 'inherit',
    fontSize: 13,
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  hold: {
    display: 'none'
  },
  choices: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    margin: [0, 'auto', 8],
    maxWidth: 640,
    textAlign: 'center'
  },
  choicePhoto: {
    display: 'block',
    width: '100%',
    height: 140,
    objectFit: 'cover',
    margin: [0, 0, 8]
  },
  choiceAdd: {
    margin: [4, 0],
    fontFamily: theme.typography.primary,
    fontSize: 18,
    color: theme.color.secondary.main
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center'
  },
  action: {
    margin: [0, 8, 10]
  },
  '@global': {
    '@keyframes arwesTrace': {
      to: { strokeDashoffset: -100 }
    }
  }
});

class Entradas extends React.Component {
  static propTypes = {
    classes: PropTypes.object
  };

  state = {
    picked: 'Preferencial',
    step: 1,
    addon: null
  };

  onPick = name => {
    this.setState({ picked: name, addon: null });
  };

  scrollOffer = () => {
    if (this.offer) {
      this.offer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  onContinue = () => {
    this.setState({ step: 2, addon: null }, this.scrollOffer);
  };

  onAddon = key => {
    this.setState({ addon: key, step: 3 }, this.scrollOffer);
  };

  onSkipAddon = () => {
    this.setState({ addon: null, step: 3 }, this.scrollOffer);
  };

  onBack = () => {
    this.setState({ step: 1, addon: null });
  };

  tier = () => tiers.find(item => item.name === this.state.picked);

  addons = tier => {
    if (tier.name === 'VIP') {
      return [
        {
          key: 'firma',
          name: 'Foto y firma',
          benefit: 'Foto y firma en el póster oficial.',
          image: images.meet,
          add: 10000,
          total: 45000,
          before: 56000,
          save: 11000,
          left: 'Quedan 20 lugares',
          href: links.meet
        },
        {
          key: 'disco',
          name: 'Foto y disco',
          benefit: 'Foto y el disco firmado.',
          image: images.meetDisco,
          add: 30000,
          total: 65000,
          before: 80000,
          save: 15000,
          left: 'Quedan 30',
          href: links.meetDisco
        }
      ];
    }

    return [
      {
        key: 'firma',
        name: 'Foto y firma',
        benefit: 'Foto y firma en el póster oficial.',
        image: images.firma,
        add: 20000,
        total: tier.amount + 20000,
        href: links.firma
      },
      {
        key: 'disco',
        name: 'Foto y disco',
        benefit: 'Foto y el disco firmado.',
        image: images.disco,
        add: 30000,
        total: tier.amount + 30000,
        left: 'Quedan 10',
        href: links.disco
      }
    ];
  };

  vipOffer = (tier, chosen) => {
    const withDisco = chosen && chosen.key === 'disco';
    const total = withDisco ? 65000 : 45000;
    const from = chosen ? chosen.total : tier.amount;

    return {
      name: withDisco ? 'VIP, meet & greet y disco' : 'VIP y meet & greet',
      benefit: tier.name === 'VIP'
        ? 'Añades el meet & greet a tu pase.'
        : 'Mejoras tu entrada a VIP y añades el meet & greet.',
      image: withDisco ? images.meetDisco : images.meet,
      total,
      before: withDisco ? 80000 : 56000,
      save: withDisco ? 15000 : 11000,
      add: total - from,
      fromLabel: chosen ? chosen.name : `Entrada ${tier.name}`,
      fromPrice: from,
      left: withDisco ? 'Quedan 30' : 'Quedan 20 lugares',
      href: withDisco ? links.meetDisco : links.meet
    };
  };

  onKeyDown = (event, name) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onPick(name);
    }
  };

  render () {
    const { classes } = this.props;
    const { picked, step, addon } = this.state;
    const tier = this.tier();
    const addons = this.addons(tier);
    const chosen = addons.find(item => item.key === addon);
    const vip = this.vipOffer(tier, chosen);

    return (
      <Main className={classes.root}>
        <Secuence stagger>
          <h1><Text>Acceso anticipado</Text></h1>
          <p className={classes.intro}>
            <Text>Preventa Fase 1. El 1 de noviembre suben los precios.</Text>
          </p>
          <div className={classes.steps}>
            <span className={cx(classes.step, step === 1 && classes.stepOn)}>1 · Entrada</span>
            <span className={cx(classes.step, step === 2 && classes.stepOn)}>2 · Agregar</span>
            <span className={cx(classes.step, step === 3 && classes.stepOn)}>3 · VIP</span>
          </div>
          <Fader className={cx(classes.block, step !== 1 && classes.hold)}>
              <div className={classes.grid}>
                {tiers.map(item => (
                  <article
                    key={item.name}
                    className={cx(classes.card, item.name === 'VIP' && classes.vip, item.name === tier.name && classes.picked)}
                    role='button'
                    tabIndex={0}
                    aria-pressed={item.name === tier.name}
                    onClick={() => this.onPick(item.name)}
                    onKeyDown={event => this.onKeyDown(event, item.name)}
                  >
                    {item.name === 'VIP' && (
                      <svg className={classes.frame} viewBox='0 0 100 100' preserveAspectRatio='none' aria-hidden='true'>
                        <rect x='1' y='1' width='98' height='98' pathLength='100' />
                      </svg>
                    )}
                    <p className={classes.zone}>{item.zone}</p>
                    <h2>{item.name}</h2>
                    <p className={classes.price}>{item.price}</p>
                    <p className={classes.next}>{item.next} · Fase 2</p>
                    <p>{item.detail}</p>
                  </article>
                ))}
              </div>
              <p>
                <Button onClick={this.onContinue}>Continuar</Button>
              </p>
          </Fader>
          {step === 2 && (
            <div className={classes.upsell} ref={ref => (this.offer = ref)}>
              <p className={classes.order}>
                <span>Tu entrada · {tier.name} · {tier.price}</span>
                <button type='button' className={classes.back} onClick={this.onBack}>Cambiar</button>
              </p>
              <div className={classes.choices}>
                {addons.map(item => (
                  <article key={item.key} className={classes.card}>
                    {item.left && <p className={classes.upsellKicker}>{item.left}</p>}
                    <img className={classes.choicePhoto} src={item.image} alt={item.name} />
                    <h2 className={classes.upsellTitle}>{item.name}</h2>
                    <p>{item.benefit}</p>
                    <p className={classes.choiceAdd}>Por {money(item.add)} más</p>
                    <p className={classes.summaryPrice}>Total {money(item.total)}</p>
                    {item.save && <p className={classes.save}>Ahorras {money(item.save)}</p>}
                    <p>
                      <button type='button' className={classes.accept} onClick={() => this.onAddon(item.key)}>
                        Elegir
                      </button>
                    </p>
                  </article>
                ))}
              </div>
              <p className={classes.skip}>
                <button type='button' className={classes.back} onClick={this.onSkipAddon}>No, gracias</button>
              </p>
            </div>
          )}
          {step === 3 && (
            <div className={classes.upsell} ref={ref => (this.offer = ref)}>
              <p className={classes.order}>
                <span>{chosen ? `${chosen.name} · ${money(chosen.total)}` : `Tu entrada · ${tier.name} · ${tier.price}`}</span>
                <button type='button' className={classes.back} onClick={this.onBack}>Cambiar</button>
              </p>
              <p className={classes.upsellKicker}>{vip.left}</p>
              <img className={classes.photo} src={vip.image} alt={vip.name} />
              <h2 className={classes.upsellTitle}>{vip.name}</h2>
              <p className={classes.upsellText}>{vip.benefit}</p>
              <p className={classes.total}>
                <span className={classes.before}>{money(vip.before)}</span>
                {money(vip.total)}
              </p>
              <p className={classes.save}>Ahorras {money(vip.save)}</p>
              <p className={classes.choiceAdd}>Por {money(vip.add)} más</p>
              <div className={classes.rows}>
                <p className={classes.row}><span>{vip.fromLabel}</span><span>{money(vip.fromPrice)}</span></p>
                <p className={classes.row}><span>Se agrega</span><span>{money(vip.add)}</span></p>
                <p className={cx(classes.row, classes.rowTotal)}><span>Total</span><span>{money(vip.total)}</span></p>
              </div>
              <p>
                <a className={classes.accept} href={vip.href}>{`Agregar a mi entrada · ${money(vip.total)}`}</a>
              </p>
              <p className={classes.note}>Sigues en la ficha para completar la compra.</p>
              <p className={classes.skip}>
                <a className={classes.back} href={chosen ? chosen.href : links.ticket}>
                  {chosen ? `No, gracias. Seguir con ${chosen.name.toLowerCase()}.` : 'No, gracias'}
                </a>
              </p>
            </div>
          )}
        </Secuence>
      </Main>
    );
  }
}

export default withStyles(styles)(Entradas);
